import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import "./PropertyBankTable.scss";
import { IPropertyBankData } from "./PropertyBankList";
import TableHeaderRow from "../../../../components/tables/TableHeaderRow";
import TableHeaderCell from "../../../../components/tables/TableHeaderCell";
import TableDataRow from "../../../../components/tables/TableDataRow";
import TableDataCell from "../../../../components/tables/TableDataCell";
import ProfileImage from "../../../../components/profileImage/ProfileImage";
import ActionDialog from "../../actionIcon/ActionDialog";

const PropertyBankTable = ({ listData }: { listData: IPropertyBankData[] }) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const formatDate = (dateString: string) => {
    return moment(dateString).format("MMMM D, YYYY");
  };

  const formatTime = (dateString: string) => {
    return moment(dateString).format("hh:mm A");
  };

  const formatStatus = (status: string) => {
    if (!status) return "";
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getCreatorRoleLabel = (role: string | undefined) => {
    if (!role) return "User";
    if (role === "admins") return "Admin";
    if (role === "agents") return "Agent";
    // Capitalize first letter if it's something else
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const getStatusClass = (status: string) => {
    if (status === "agent_validated") return "status-validated";
    if (status === "admin_draft") return "status-draft";
    return "status-draft";
  };

  const handleRowClick = (id: string) => {
    navigate(`/property-bank/view/${id}`);
  };

  const handleAgentClick = (e: React.MouseEvent, agentId: string) => {
    e.stopPropagation();
    if (agentId) {
      navigate(`/agents/view/${agentId}?type=info`);
    }
  };

  const handleAgencyClick = (e: React.MouseEvent, agencyId: string) => {
    e.stopPropagation();
    if (agencyId) {
      navigate(`/agencies/view/${agencyId}?type=info`);
    }
  };

  const handleCreatorClick = (
    e: React.MouseEvent,
    creatorId: string,
    role: string | undefined,
  ) => {
    e.stopPropagation();
    if (!creatorId) return;

    if (role === "agents") {
      navigate(`/agents/view/${creatorId}?type=info`);
    } else {
      navigate(`/users/view/${creatorId}?type=info`);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="property-bank-table-container">
      <table>
        <TableHeaderRow>
          <TableHeaderCell minWidth={220}>ADDRESS</TableHeaderCell>
          <TableHeaderCell minWidth={140}>PROPERTY TYPE</TableHeaderCell>
          <TableHeaderCell minWidth={220}>AGENT</TableHeaderCell>
          <TableHeaderCell minWidth={180}>AGENCY</TableHeaderCell>
          <TableHeaderCell minWidth={200}>CREATED BY</TableHeaderCell>
          <TableHeaderCell minWidth={180}>PROPERTY ID</TableHeaderCell>
          <TableHeaderCell minWidth={150}>STATUS</TableHeaderCell>
          <TableHeaderCell minWidth={160}>CREATED ON</TableHeaderCell>
          <TableHeaderCell minWidth={160}>UPDATED ON</TableHeaderCell>
          <TableHeaderCell minWidth={80}>ACTION</TableHeaderCell>
        </TableHeaderRow>

        {listData?.map((data) => (
          <div
            key={data._id}
            className="row-wrapper"
            onClick={() => handleRowClick(data._id)}
            style={{ cursor: "pointer" }}
          >
            <TableDataRow className="contain-row">
              <TableDataCell minWidth={220} className="data-cell-contain">
                <div className="address-contain">
                  <p className="address-line-1 font-inter-400">
                    {data.property_address}
                  </p>
                </div>
              </TableDataCell>

              <TableDataCell minWidth={140} className="data-cell-contain">
                <div className="property-type font-inter-400">
                  {data.property_type_ref?.name || "-"}
                </div>
              </TableDataCell>

              <TableDataCell minWidth={220} className="data-cell-contain">
                <div className="agent-contain">
                  {data.agent_ref && (
                    <div className="agent-image">
                      <ProfileImage
                        uploadRecord={data.agent_ref?.profile_image_ref}
                        url={data.agent_ref?.image || ""}
                        width={45}
                        height={45}
                      />
                    </div>
                  )}
                  <div className="agent-details">
                    <p className="agent-name">
                      <span
                        className="clickable-link"
                        onClick={(e) =>
                          handleAgentClick(e, data.agent_ref?._id || "")
                        }
                      >
                        {data.agent_ref?.name || "-"}
                      </span>
                    </p>
                    <p
                      className="agent-email font-inter-400"
                      title={data.agent_ref?.email}
                    >
                      {data.agent_ref?.email}
                    </p>
                  </div>
                </div>
              </TableDataCell>

              <TableDataCell minWidth={180} className="data-cell-contain">
                <div className="agency-name font-inter-400">
                  <span
                    className="clickable-link"
                    onClick={(e) =>
                      handleAgencyClick(e, data.agency_ref?._id || "")
                    }
                  >
                    {data.agency_ref?.agency_legal_name || "-"}
                  </span>
                </div>
              </TableDataCell>

              <TableDataCell minWidth={200} className="data-cell-contain">
                <div className="creator-contain">
                  <div className="creator-image">
                    <ProfileImage
                      uploadRecord={data.creator_id?.profile_image_ref}
                      url={data.creator_id?.profile_image || ""}
                      width={45}
                      height={45}
                    />
                  </div>
                  <div className="creator-details">
                    <span className="role-badge font-inter-400">
                      {getCreatorRoleLabel(data.creator_role)}
                    </span>
                    <p className="creator-name">
                      <span
                        className="clickable-link"
                        onClick={(e) =>
                          handleCreatorClick(
                            e,
                            data.creator_id?._id || "",
                            data.creator_role,
                          )
                        }
                      >
                        {data.creator_id?.fullname ||
                          `${data.creator_id?.first_name || ""} ${
                            data.creator_id?.last_name || ""
                          }`.trim() ||
                          "-"}
                      </span>
                    </p>
                  </div>
                </div>
              </TableDataCell>

              <TableDataCell minWidth={180} className="data-cell-contain">
                <p className="property-id-text font-inter-400">
                  {data.property_ref || "-"}
                </p>
              </TableDataCell>

              <TableDataCell minWidth={150} className="data-cell-contain">
                <div
                  className={`status-badge font-inter-400 ${getStatusClass(
                    data.status,
                  )}`}
                >
                  {formatStatus(data.status)}
                </div>
              </TableDataCell>

              <TableDataCell minWidth={160} className="data-cell-contain">
                <div className="date-contain">
                  <p className="date-text font-inter-400">
                    {formatDate(data.createdAt)}
                  </p>
                  <p className="time-text font-inter-400">
                    {formatTime(data.createdAt)}
                  </p>
                </div>
              </TableDataCell>

              <TableDataCell minWidth={160} className="data-cell-contain">
                <div className="date-contain">
                  <p className="date-text font-inter-400">
                    {formatDate(data.updatedAt)}
                  </p>
                  <p className="time-text font-inter-400">
                    {formatTime(data.updatedAt)}
                  </p>
                </div>
              </TableDataCell>

              <TableDataCell minWidth={80} className="action-td">
                <div
                  className="action-menu-wrapper"
                  ref={openMenuId === data._id ? menuRef : null}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="action-menu-btn"
                    onClick={() =>
                      setOpenMenuId(openMenuId === data._id ? null : data._id)
                    }
                  >
                    &#8942;
                  </div>

                  {openMenuId === data._id && (
                    <div className="action-dropdown">
                      <div
                        className="action-item font-inter-400"
                        onClick={() =>
                          navigate(`/property-bank/view/${data._id}`)
                        }
                      >
                        View
                      </div>
                    </div>
                  )}
                </div>
              </TableDataCell>
            </TableDataRow>
          </div>
        ))}
      </table>
      <ActionDialog />
    </div>
  );
};

export default PropertyBankTable;
